import React, {
  useEffect,
  useReducer,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import api from '../api';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { useParams } from 'react-router-dom';
import { getError, checkAndReplaceInactiveFonts } from '../utils';
import {
  designsOnLayouts,
  uniqueDesignIds,
  uniqueLayoutIds,
  uniqueProfiles,
} from '../utils/storedDesigns';
import { unavailableUserIconAssetIds } from '../utils/userIconAccess';
import {
  orderReplacedFontsReport,
  replacedFontsNumbers,
} from '../utils/fontReplacementReport';
import { useUserIconAssets } from '../utils/useUserIconAssets';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { Container, Modal, Form, Badge } from 'react-bootstrap';
import PrintingRunSvg from '../components/customizer/shared/processOrderScreen/PrintingRunSvg';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import { fetchFont } from '../utils/fontHelpers';
import {
  buildPunchingHolePositions,
  PRINTING_SHEET,
} from '../utils/moldGeometry';
import { svgToString } from '../utils/exportHelpers';
import {
  preparePrintingSvg,
  printingSettingsWarning,
} from '../utils/printingFiles';
import Table from 'react-bootstrap/Table';
import { calculateKeycapSizes } from '../utils/keycapSizes';
import { migrateDesignIfNeeded } from '../utils/customizerCompat';
import { collectUserIconAssetIds } from '../utils/userIconAssets';
import { useTranslation } from 'react-i18next';
import {
  designWithPrintingRuns,
  moldPrintSettings,
  moldSlots,
} from '../utils/moldAssignment';

const reducer = (state, action) => {
  // reducer function to update the state for different cases
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, order: action.payload };
    case 'ORDER_ITEM_UPDATE_SUCCESS':
      // Update the specific orderItem within the order
      const updatedOrderItems = state.order.orderItems.map((item) =>
        item._id === action.payload._id ? action.payload : item
      );
      return {
        ...state,
        order: { ...state.order, orderItems: updatedOrderItems },
      };
    case 'DESIGNS_FETCH_SUCCESS':
      return { ...state, designs: action.payload, loading: false };
    case 'LAYOUTS_FETCH_SUCCESS':
      return { ...state, layouts: action.payload, loading: false };
    case 'DESIGNS_WITH_LAYOUTS_FETCH_SUCCESS':
      return { ...state, designs: action.payload, loading: false };
    case 'MOLDS_FETCH_SUCCESS':
      return {
        ...state,
        moldDocsById: action.payload.byId, // stores mold metadata by mold document id
        profileToMoldsMap: action.payload.profileToMoldsMap, // stores usable mold slot data grouped by profile
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function ProcessOrderScreen() {
  const [{ loading, error, order, designs, moldDocsById }, dispatch] =
    useReducer(reducer, {
      loading: true,
      order: {},
      designs: [],
      moldDocsById: {},
      error: '',
    }); // useReducer function instead of useState hook, to manage complex state. Gets the states (loading, oder and error) and dispatch, to dispatch the actions from the reducer above

  const { t } = useTranslation();

  const params = useParams(); // extracts the URL parameters from the current route
  const { orderId } = params; // uses ES6 object destructuring to extract the id property from params

  const [showReplacementsModal, setShowReplacementsModal] = useState(false);
  const [replacedFontsSummary, setReplacedFontsSummary] = useState([]);
  const [show, setShow] = useState(false);
  const [selectedThumbnail, setSelectedThumbnail] = useState(''); // Store the thumbnail URL
  const [showPunchingHoles, setShowPunchingHoles] = useState(true);
  const [showMoldBorder, setShowMoldBorder] = useState(true);
  const [showScrewHoles, setShowScrewHoles] = useState(false);
  const [showShadows, setShowShadows] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const [show2x2Grid, setShow2x2Grid] = useState(false);
  const [show3x3Grid, setShow3x3Grid] = useState(false);
  const [showPrintableArea, setShowPrintableArea] = useState(false);
  const [showTopSurface, setShowTopSurface] = useState(false);
  const [triggerKeycapShadowRerender, setTriggerKeycapShadowRerender] =
    useState(true);
  const [fonts, setFonts] = useState({}); // store loaded fontkit font objects
  const fontPromises = useRef({}); // in-flight fetches
  const [keycapFilter, setKeycapFilter] = useState('Uniform');
  const [keycapFrequenciesByLayoutId, setKeycapFrequenciesByLayoutId] =
    useState({}); // stores the keycap frequencies for each layout (the number of keycaps needed for each order item)

  const loadFont = useCallback(
    (cacheKey, apiUrl) =>
      fetchFont(cacheKey, apiUrl, fonts, fontPromises.current, setFonts),
    [fonts]
  ); // loads and caches a font once (fetchFont in utils/fontHelpers.js, one copy in four screens until chain 10, Oct 2026): the loaded fonts from the state, the fetches in flight from the ref

  useEffect(() => {
    const fetchOrderAndData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data: orderData } = await api.get(`/api/orders/${orderId}`);
        dispatch({ type: 'FETCH_SUCCESS', payload: orderData });

        const designIds = uniqueDesignIds(orderData.orderItems); // the designs the order's items reference, each once

        // Fetch all designs for these designIds:
        let designsData = [];
        if (designIds.length > 0) {
          const { data: fetchedDesigns } = await api.post(
            '/api/designs/multiple',
            { designIds }
          );
          designsData = fetchedDesigns;
          dispatch({ type: 'DESIGNS_FETCH_SUCCESS', payload: designsData });
        }

        designsData = await fetchSignedUrls(designsData); // Fetch signed URLs and merge them into designsData

        const { sanitizedDesignsData, replacedFontsSummary } =
          await checkAndReplaceInactiveFonts({
            designsData,
          });

        if (replacedFontsSummary.length > 0) {
          setReplacedFontsSummary(replacedFontsSummary);
          toast.warning(
            'One or more inactive fonts were replaced with a fallback font.'
          );
          setShowReplacementsModal(true);
        }

        const layoutIds = uniqueLayoutIds(sanitizedDesignsData); // the layouts the designs sit on, each once

        // Fetch all layouts for these layoutIds:
        let layoutsData = [];
        if (layoutIds.length > 0) {
          const { data: fetchedLayouts } = await api.post(
            '/api/layouts/multiple',
            { layoutIds }
          );
          layoutsData = fetchedLayouts;
          dispatch({ type: 'LAYOUTS_FETCH_SUCCESS', payload: layoutsData });
        }

        const updatedDesignsData = designsOnLayouts(
          sanitizedDesignsData,
          layoutsData
        ); // each design's stored keycaps merged over its layout's keycaps and the keycap defaults, with the spacers put back and the row/col identity for the printing files

        const preparedDesignsData = updatedDesignsData.map((design) =>
          migrateDesignIfNeeded(design)
        ); // migrate each design to the current customizer version

        // Trigger a rerender of the keycap shadows:
        setTriggerKeycapShadowRerender((prev) => !prev);

        dispatch({
          type: 'DESIGNS_WITH_LAYOUTS_FETCH_SUCCESS',
          payload: preparedDesignsData,
        });

        // Fetch molds and assign keycaps to molds
        // 1. Extract profiles from preparedDesignsData:
        const profiles = uniqueProfiles(preparedDesignsData); // the profiles the designs use, each once

        // 2. Fetch molds for each profile (to avoid multiple fetches for same profile):
        const profileToMoldsMap = {};
        const moldDocsById = {};
        for (const profile of profiles) {
          const { data: molds } = await api.get(
            `/api/molds/profile/${profile}`
          );
          profileToMoldsMap[profile] = moldSlots(molds); // for the profileToMoldsMap only store the _id and 'mold' field
          Object.assign(moldDocsById, moldPrintSettings(molds)); // keep the scaling and the punchingHoleXAdjustments for each mold, so that I can use them to apply to the punching holes
        }

        dispatch({
          type: 'MOLDS_FETCH_SUCCESS',
          payload: {
            byId: moldDocsById,
            profileToMoldsMap,
          },
        });

        // build quick lookups for 'orderItem -> designId' and 'layout _id -> layout':
        const orderItemByDesignId = Object.fromEntries(
          (orderData.orderItems || [])
            .filter((it) => it.designId)
            .map((it) => [String(it.designId), it])
        );
        const layoutById = Object.fromEntries(
          layoutsData.map((l) => [String(l._id), l])
        );

        // calculate keycap frequencies for each layout and store them in state:
        const keycapFrequenciesByLayoutId = Object.fromEntries(
          Object.entries(layoutById)
            .filter(([, layoutDoc]) => Array.isArray(layoutDoc.layout))
            .map(([layoutId, layoutDoc]) => [
              layoutId,
              calculateKeycapSizes(layoutDoc.layout),
            ])
        );
        setKeycapFrequenciesByLayoutId(keycapFrequenciesByLayoutId);

        // 3. Assign keycaps to molds for each design:
        const finalDesignsData = preparedDesignsData.map((design) => {
          const { design: designWithRuns, unassignedKeycaps } =
            designWithPrintingRuns(
              design,
              layoutById[String(design.layoutEditor.id)], // find the layout for this design
              orderItemByDesignId[String(design._id)], // find the order item for this design
              profileToMoldsMap
            ); // the design with its printing runs on the molds of its profile (the pooling flag attached for the UI), and the keycaps no slot took

          if (unassignedKeycaps.length > 0) {
            // If some keycaps remain, they couldn't be assigned to any slot
            toast.warning(
              'Not all keycaps could be assigned to the available molds.'
            );
          }

          return designWithRuns;
        });

        dispatch({
          type: 'DESIGNS_WITH_LAYOUTS_FETCH_SUCCESS',
          payload: finalDesignsData,
        });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };

    // Fetch the order if it's not loaded or doesn't match the current orderId:
    if (!order.orderId || (order.orderId && order.orderId !== orderId)) {
      fetchOrderAndData();
    }
  }, [order]);

  const fetchSignedUrls = async (designsData) => {
    try {
      const designIds = designsData.map((design) => design._id);
      if (designIds.length === 0) return designsData; // Return unchanged if no designs

      const { data: signedUrls } = await api.post('/api/designs/thumbnails', {
        designIds,
      });

      // Merge signed URLs into the designsData array
      return designsData.map((design) => ({
        ...design,
        metadata: {
          ...design.metadata,
          signedThumbnailUrl: signedUrls.thumbnails?.[design._id] || '', // Access the correct nested object
        },
      }));
    } catch (err) {
      console.error('Error fetching signed URLs:', err);
      return designsData; // Return original designs if error occurs
    }
  };

  const handleShow = (thumbnail) => {
    setSelectedThumbnail(thumbnail); // Update selected thumbnail
    setShow(true); // Show modal
  };

  const handleClose = () => setShow(false);

  const ensureUserIconAssetsForDesign = async (design) => {
    const assetIds = collectUserIconAssetIds(design); // the uploaded icon asset ids used by this design, each once

    if (assetIds.length === 0) {
      return true; // design does not use uploaded icons
    }

    const cachedAssetsById = { ...userIconAssetsById }; // copy the current cache so newly fetched assets can be checked immediately in this function

    const missingAssetIds = assetIds.filter(
      (assetId) => !cachedAssetsById[assetId]
    );

    if (missingAssetIds.length > 0) {
      const fetchedAssets = await fetchAndMergeUserIconAssets(missingAssetIds);

      fetchedAssets.forEach((asset) => {
        cachedAssetsById[String(asset.assetId)] = asset;
      });
    }

    const unavailableAssetIds = unavailableUserIconAssetIds(
      assetIds,
      cachedAssetsById
    ); // the icons a printing file can't embed: a PNG without its signed link, an SVG without its markup, an asset the backend didn't return

    if (unavailableAssetIds.length > 0) {
      console.error('Unavailable uploaded icon assets:', unavailableAssetIds);

      toast.error(
        'Printing files cannot be downloaded because one or more uploaded icons could not be loaded.'
      );

      return false;
    }

    return true;
  }; // verifies that all uploaded icons used by the design are available before creating printing files

  async function downloadAllPrintingFilesAsZip(currentDesign, fonts, fileType) {
    try {
      // guard: fileType must be 'svg' or 'pdf'
      if (fileType !== 'svg' && fileType !== 'pdf') {
        console.error(`Invalid fileType '${fileType}'. Use 'svg' or 'pdf'.`);
        return;
      }

      // guard: ensure we have work to do
      const runs = currentDesign?.moldAssignments || [];
      if (!runs.length) {
        console.error('No mold assignments found for current design.');
        return;
      }

      const canUseUploadedIcons =
        await ensureUserIconAssetsForDesign(currentDesign); // checks that all uploaded icons in this design are available before creating printing files
      if (!canUseUploadedIcons) {
        return; // stop before generating incomplete printing SVG/PDF files
      }

      const zip = new JSZip(); // create a new ZIP archive in memory

      const rootName = `order_${orderId} design_${currentDesign._id}`; // root folder name
      const root = zip.folder(rootName);

      // subfolders inside the root folder:
      const svgFolder = root.folder('svg');
      const pdfFolder = root.folder('pdf');

      const outFolder = fileType === 'svg' ? svgFolder : pdfFolder; // depending on fileType, the files are added to the correct folder (svg files to the svg folder and pdf files to the pdf folder)

      for (let runIndex = 0; runIndex < runs.length; runIndex++) {
        const svgEl = document.getElementById(
          `printingSVG-${currentDesign._id}-${runIndex}`
        );
        if (!svgEl) {
          console.warn(
            `SVG with id printingSVG-${currentDesign._id}-${runIndex} not found.`
          );
          continue; // move on if the SVG element for this run isn't found
        }

        const clone = await preparePrintingSvg(svgEl, fonts); // the run's SVG cloned and made into the file's content: texts to paths, uploaded PNGs embedded, every alpha split off for Inkscape

        if (fileType === 'svg') {
          // serialize and add to zip
          const svgStr = svgToString(clone, { withDoctype: true });
          outFolder.file(`run-${runIndex + 1}.svg`, svgStr); // add the SVG string as a file to the ZIP
        } else {
          // render to PDF via svg2pdf + jsPDF
          const svgStr = svgToString(clone, { withDoctype: false });

          // create a hidden DOM node so svg2pdf can read the element
          const hidden = document.createElement('div');
          hidden.style.visibility = 'hidden';
          hidden.style.position = 'absolute';
          hidden.style.top = '-10000px';
          hidden.innerHTML = svgStr;
          document.body.appendChild(hidden);

          const svgForPdf = hidden.querySelector('svg');

          const pdf = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [PRINTING_SHEET.width, PRINTING_SHEET.height], // the sheet's exact size: jsPDF's named a3 is rounded to two decimal points, 0.4 µm short (user decision, Nov 2026)
            compress: false,
          });

          await svg2pdf(svgForPdf, pdf, {
            x: 0,
            y: 0,
            width: PRINTING_SHEET.width,
            height: PRINTING_SHEET.height,
          });

          document.body.removeChild(hidden); // remove the hidden container

          const pdfBlob = pdf.output('blob');
          outFolder.file(`run-${runIndex + 1}.pdf`, pdfBlob); // add the PDF blob to the ZIP
        }
      }

      // build zip and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${rootName}.zip`);
    } catch (err) {
      console.error('Error during multiple file ZIP download:', err);

      const errorMessage = getError(err);
      const errorDetails = err?.response?.data?.details;

      toast.error(
        errorMessage
          ? t(errorMessage, { details: errorDetails })
          : 'Printing files could not be downloaded.'
      );
    }
  } // this function downloads all printing svg files of a custom designed keycap design onto the users computer and converts texts to paths

  const updateOrderItemStatus = async (orderItemId, newStatus) => {
    try {
      const { data } = await api.put(
        `/api/orders/order-item/status/${order.orderId}/${orderItemId}`,
        { status: newStatus }
      );
      toast.success(`Order item status updated to ${newStatus}`);

      // update the status also in the components state:
      const updatedOrderItem = data.order.orderItems.find(
        (item) => item._id === orderItemId
      );
      dispatch({
        type: 'ORDER_ITEM_UPDATE_SUCCESS',
        payload: updatedOrderItem,
      });
    } catch (err) {
      toast.error(getError(err));
    }
  }; // function to update the status of an orderItem in the database

  const handleDownloadReport = () => {
    if (!replacedFontsSummary || replacedFontsSummary.length === 0) return;

    const { content, fileName } = orderReplacedFontsReport(
      replacedFontsSummary,
      order
    ); // the report's text and file name

    // 4) Convert to a Blob and trigger download
    const blob = new Blob([content], {
      type: 'text/plain;charset=utf-8',
    });

    saveAs(blob, fileName);
  }; // this function will trigger the download of the "Replaced Fonts Report" as a .txt file

  const holePositionsByMoldId = React.useMemo(() => {
    const map = {};

    Object.entries(moldDocsById).forEach(([id, doc]) => {
      // for every mold document:
      const scale = doc?.scaling?.punchingHoles ?? 1; // get the scaling value from the doc
      const xAdj = doc?.punchingHoleXAdjustments ?? {}; // get the punchingHoleXAdjustments from the doc
      map[id] = buildPunchingHolePositions(scale, xAdj); // calculate the final punching hole positions
    });

    return map;
  }, [moldDocsById]); // calculates the final punching hole positions for each mold document

  const usedUserIconAssetIds = useMemo(() => {
    const assetIds = new Set();

    (designs || []).forEach((design) => {
      collectUserIconAssetIds(design).forEach((assetId) => {
        assetIds.add(assetId);
      });
    });

    return [...assetIds];
  }, [designs]); // collect uploaded icon assetIds from all prepared designs in this order

  const {
    userIconAssetsById,
    fetchAndMergeUserIconAssets,
    refreshUserIconAsset,
  } = useUserIconAssets({
    usedUserIconAssetIds,
    signedIn: true, // an admin page: the route guard has checked the login
    t,
  }); // the render assets of the uploaded icons the order's designs show, loaded as the designs arrive and refreshed before their links expire

  const printingSettings = {
    show2x2Grid,
    show3x3Grid,
    showPrintableArea,
    showTopSurface,
    showShadows,
    showLabels,
    showPunchingHoles,
    showMoldBorder,
    showScrewHoles,
  }; // the preview's nine switches, which every run's SVG and its files follow

  const confirmPrintingDownload = (design, fileType) => {
    const warning = printingSettingsWarning(printingSettings); // the preview settings that are on, which the files would carry
    if (!warning || window.confirm(warning)) {
      downloadAllPrintingFilesAsZip(design, fonts, fileType);
    }
  }; // the download of a design's printing files, after the question about the settings that are on

  return (
    <Container>
      <title>Process Order {orderId}</title>

      <h1 className="screen-heading">Process Order {orderId}</h1>

      {loading ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <div>
          <Row>
            <Col md={12}>
              {order.orderItems.map((item, orderItemIndex) => {
                const currentDesign = designs.find(
                  (d) => d._id === item.designId
                );
                const layoutId = currentDesign?.layoutEditor?.id; // get the layout id of the current design
                const keycapsForThisLayout =
                  keycapFrequenciesByLayoutId[layoutId]; // access the keycap frequencies needed for this design/layout

                return (
                  <Card className="mb-3" key={item._id}>
                    <Card.Body className="order-card">
                      <Row>
                        <Col md={4}>
                          <strong>{item.name}</strong>
                          <p style={{ fontSize: '12px', marginBottom: '0px' }}>
                            {item.designId}
                          </p>
                          <p style={{ fontSize: '12px', marginBottom: '0px' }}>
                            {currentDesign?.name}
                          </p>
                        </Col>
                        <Col
                          md={4}
                          className="text-center"
                          style={{
                            fontWeight: '700',
                            color:
                              item.quantity > 1 &&
                              !currentDesign?.poolingEnabled
                                ? 'red'
                                : 'black',
                          }}
                        >
                          <span>
                            {currentDesign?.poolingEnabled
                              ? '1'
                              : item.quantity}{' '}
                            x
                          </span>
                        </Col>
                        <Col
                          md={4}
                          className="d-flex justify-content-end align-items-start"
                        >
                          <Button
                            variant="light"
                            onClick={() => {
                              const design = designs.find(
                                (d) => d._id === item.designId
                              );
                              handleShow(
                                design?.metadata?.signedThumbnailUrl || ''
                              );
                            }}
                          >
                            Thumbnail
                          </Button>
                          &nbsp;
                          <Button
                            variant="light"
                            onClick={() =>
                              confirmPrintingDownload(currentDesign, 'pdf')
                            }
                          >
                            PDF
                          </Button>
                          &nbsp;
                          <Button
                            variant="light"
                            onClick={() =>
                              confirmPrintingDownload(currentDesign, 'svg')
                            }
                          >
                            SVG
                          </Button>
                          &nbsp;
                          {item.status === 'pending' ? (
                            <Button
                              variant="dark"
                              type="button"
                              onClick={() =>
                                updateOrderItemStatus(item._id, 'manufacturing')
                              }
                            >
                              Manufacture
                            </Button>
                          ) : item.status === 'manufacturing' ? (
                            <Button
                              variant="dark"
                              type="button"
                              onClick={() =>
                                updateOrderItemStatus(item._id, 'finished')
                              }
                            >
                              Finished
                            </Button>
                          ) : (
                            <Badge
                              bg="success"
                              className="d-flex align-items-center"
                              style={{ fontSize: '10px' }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                className="bi bi-check"
                                viewBox="0 0 16 16"
                              >
                                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
                              </svg>
                            </Badge>
                          )}
                        </Col>
                      </Row>

                      <div className="divider"></div>

                      <div className="d-flex">
                        <div style={{ width: '80%' }}>
                          <div
                            className="d-flex justify-content-around mt-5"
                            style={{ overflow: 'scroll' }}
                          >
                            {currentDesign?.moldAssignments &&
                            currentDesign.moldAssignments.length > 0
                              ? currentDesign.moldAssignments.map(
                                  (moldRun, runIndex) => {
                                    // moldRun is an array of mold objects (which include the _id of the mold and an array of keycaps assigned to that particular mold instance)
                                    // We can pass keycaps directly as keycapData to our components
                                    const { moldDocId, keycaps } = moldRun;
                                    const holePos =
                                      holePositionsByMoldId[moldDocId]; // get the punching hole positions from holePositionsByMoldId {topLeft:{cx,cy}, ...}

                                    return (
                                      <div
                                        key={`${currentDesign._id}-moldRun-${runIndex}`}
                                        style={{ marginBottom: '20px' }}
                                      >
                                        <h4 className="text-center">
                                          {runIndex + 1}
                                        </h4>
                                        <PrintingRunSvg
                                          designId={currentDesign._id}
                                          runIndex={runIndex}
                                          keycaps={keycaps}
                                          holePositions={holePos}
                                          {...printingSettings}
                                          fonts={fonts}
                                          setFonts={setFonts}
                                          loadFont={loadFont}
                                          userIconAssetsById={
                                            userIconAssetsById
                                          }
                                          refreshUserIconAsset={
                                            refreshUserIconAsset
                                          }
                                          orderItemIndex={orderItemIndex}
                                          triggerKeycapShadowRerender={
                                            triggerKeycapShadowRerender
                                          }
                                        />
                                      </div>
                                    );
                                  }
                                )
                              : null}
                          </div>

                          <div className="d-flex justify-content-end">
                            <Form.Check
                              type="checkbox"
                              label="Shadows"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={showShadows}
                              onChange={() => setShowShadows(!showShadows)}
                            />
                            <Form.Check
                              type="checkbox"
                              label="Labels"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={showLabels}
                              onChange={() => setShowLabels(!showLabels)}
                            />
                            <Form.Check
                              type="checkbox"
                              label="2x2 Grid"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={show2x2Grid}
                              onChange={() => setShow2x2Grid(!show2x2Grid)}
                            />
                            <Form.Check
                              type="checkbox"
                              label="3x3 Grid"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={show3x3Grid}
                              onChange={() => setShow3x3Grid(!show3x3Grid)}
                            />
                            <Form.Check
                              type="checkbox"
                              label="Printable Area"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={showPrintableArea}
                              onChange={() =>
                                setShowPrintableArea(!showPrintableArea)
                              }
                            />
                            <Form.Check
                              type="checkbox"
                              label="Top Surface"
                              style={{ fontSize: '10px' }}
                              checked={showTopSurface}
                              onChange={() =>
                                setShowTopSurface(!showTopSurface)
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-end">
                            <Form.Check
                              type="checkbox"
                              label="Punching Holes"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={showPunchingHoles}
                              onChange={() =>
                                setShowPunchingHoles(!showPunchingHoles)
                              }
                            />
                            <Form.Check
                              type="checkbox"
                              label="Mold Border"
                              style={{ fontSize: '10px', marginRight: '15px' }}
                              checked={showMoldBorder}
                              onChange={() =>
                                setShowMoldBorder(!showMoldBorder)
                              }
                            />
                            <Form.Check
                              type="checkbox"
                              label="Screw Holes"
                              style={{ fontSize: '10px' }}
                              checked={showScrewHoles}
                              onChange={() =>
                                setShowScrewHoles(!showScrewHoles)
                              }
                            />
                          </div>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            fontSize: '12px',
                            width: '20%',
                            marginLeft: '50px',
                          }}
                        >
                          <Table
                            striped
                            bordered
                            hover
                            style={{ marginBottom: '0px' }}
                          >
                            <thead>
                              <tr>
                                <th>Keycap</th>
                                <th>Frequency</th>
                              </tr>
                            </thead>
                            <tbody>
                              {keycapFilter === 'Uniform'
                                ? keycapsForThisLayout?.keycapSizes.map(
                                    ([size, frequency]) => (
                                      <tr key={size}>
                                        <td>{size}</td>
                                        <td>{frequency}</td>
                                      </tr>
                                    )
                                  )
                                : keycapsForThisLayout?.keycapSizesWithRow.map(
                                    ([size, frequency]) => (
                                      <tr key={size}>
                                        <td>{size}</td>
                                        <td>{frequency}</td>
                                      </tr>
                                    )
                                  )}
                            </tbody>
                          </Table>

                          <div className="d-flex justify-content-end">
                            <Button
                              className="mt-2"
                              onClick={() => setKeycapFilter('Uniform')}
                              variant={
                                keycapFilter === 'Uniform' ? 'dark' : 'light'
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="currentColor"
                                className="bi bi-list"
                                viewBox="0 0 16 16"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                                />
                              </svg>
                            </Button>
                            <Button
                              className="mx-2"
                              onClick={() => setKeycapFilter('Row')}
                              variant={
                                keycapFilter === 'Row' ? 'dark' : 'light'
                              }
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="22"
                                height="22"
                                fill="currentColor"
                                className="bi bi-list-ol"
                                viewBox="0 0 16 16"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"
                                />
                                <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z" />
                              </svg>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}

              <Modal
                show={show}
                onHide={handleClose}
                centered
                className="thumbnail-modal"
              >
                <Modal.Body>
                  <img src={selectedThumbnail} alt="Selected Thumbnail" />
                </Modal.Body>

                <Modal.Footer>
                  <Button variant="secondary" onClick={handleClose}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </Col>
          </Row>

          <Modal
            show={showReplacementsModal}
            centered
            className="replaced-fonts-modal"
          >
            <Modal.Header
              style={{ flexDirection: 'column-reverse', alignItems: 'start' }}
            >
              {(() => {
                // Check if any text was deleted across ALL designs
                const foundDeleted = replacedFontsSummary.some(
                  (designInfo) =>
                    replacedFontsNumbers(designInfo).deletedCount > 0
                ); // the report's count per design, as the customizer's header reads it (utils/fontReplacementReport.js; the header and the body held their own loops until chain 10, Oct 2026; the old nested some stopped at the first deleted text, which a few designs per order don't need)

                return (
                  <>
                    {foundDeleted && (
                      <p className="mb-0" style={{ color: 'red' }}>
                        Some text entries were removed entirely because they
                        contain glyphs that are not supported by the fallback
                        font. You can find them below.
                      </p>
                    )}
                    <p className="mb-0">
                      Some fonts in the designs are no longer available and were
                      replaced with a fallback. Below are the text entries that
                      changed.
                    </p>
                    <Modal.Title>Replaced Inactive Fonts</Modal.Title>
                  </>
                );
              })()}
            </Modal.Header>

            <Modal.Body>
              {replacedFontsSummary.map((designInfo) => {
                // 2) Collect replaced fonts & count deletions for this design
                const { replacedFonts: replacedFontsArray, deletedCount } =
                  replacedFontsNumbers(designInfo);

                const orderItem = order.orderItems.find(
                  (i) => i.designId === designInfo.designId
                );

                return (
                  <div
                    key={designInfo.designId}
                    style={{
                      paddingBottom: '2rem',
                      marginBottom: '2rem',
                      borderBottom: '1px solid black',
                    }}
                  >
                    <h4 className="mb-0" style={{ fontSize: '18px' }}>
                      <strong>{orderItem.name}</strong>
                    </h4>
                    <p className="mb-0" style={{ fontSize: '12px' }}>
                      {designInfo.designId}
                    </p>
                    <p style={{ fontSize: '12px' }}>{designInfo.designName}</p>

                    {/* Summarized info: */}
                    <p
                      style={{
                        paddingLeft: '20px',
                        fontSize: '12px',
                        fontStyle: 'italic',
                        marginBottom: deletedCount > 0 ? '0px' : '10px',
                      }}
                    >
                      Inactive fonts replaced:{' '}
                      <strong>{replacedFontsArray.join(', ')}</strong>
                    </p>

                    {/* Show how many text entries were deleted */}
                    {deletedCount > 0 && (
                      <p
                        style={{
                          paddingLeft: '20px',
                          fontSize: '12px',
                          fontStyle: 'italic',
                        }}
                      >
                        Number of text entries removed entirely:{' '}
                        <strong>{deletedCount}</strong>
                      </p>
                    )}

                    {/* Detailed info: */}
                    {designInfo.replacedKeycaps.map((keycap, index) => (
                      <div
                        key={`${designInfo.designId}-${keycap.rowIndex}-${keycap.colIndex}-${index}`}
                        style={{ paddingLeft: '20px', fontSize: '12px' }}
                      >
                        <strong>
                          {index + 1}. Keycap at Row {keycap.rowIndex}, Column{' '}
                          {keycap.colIndex}:
                        </strong>
                        <ul>
                          {keycap.replacedTexts.map((textItem, tIdx) => {
                            // If wasDeleted => show "deleted" message
                            if (textItem.wasDeleted) {
                              return (
                                <li key={tIdx} style={{ color: 'red' }}>
                                  Deleted text entry "
                                  <em>{textItem.textValue}</em>" because the
                                  fallback font does not support at least one
                                  glyph in this text. (Original Font: "
                                  <em>{textItem.originalFontFamily}</em>
                                  ")
                                </li>
                              );
                            } else {
                              // Normal replaced scenario
                              return (
                                <li key={tIdx}>
                                  Replaced font "
                                  <em>{textItem.originalFontFamily}</em>" for
                                  text "<em>{textItem.textValue}</em>"
                                </li>
                              );
                            }
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowReplacementsModal(false)}
              >
                Close
              </Button>
              <Button variant="primary" onClick={handleDownloadReport}>
                Download Report
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </Container>
  );
}
